import { SiteUrls } from '@tableau/shared-urls';
import { BodyCell } from '@tableau/widgets-datagrid';
import React from 'react';

import { Messages } from '../../../../../gen-src/Localize';

import { FieldDataType, FieldOrderField } from '../../../../../gen-src/queries';
import {
  ColumnFactory,
  createIconCellFormatter,
  createLabelColumnFormatter,
  createSelectionColumn,
  createSortableHeaderFormatter,
  createTextCellFormatter,
  ExplorerStyledDisplayGrid,
  FeatureFlagsContext,
  GraphLabel,
  GraphQLError,
  GridSortProps,
  LabelContent,
  reportGraphQLErrors,
  SelectionProviderChildProps,
  SiteUrlsContext,
  TypedBodyCellProps,
  TypedColumn,
  UnsortableColumnKey,
  UseRenderLabelsParams,
  UsualDisplayGridProps,
} from '../../../data-catalog-shared';
import { getLabelParamsForGrids, labelCategoryToString, LabelGridWrapperProps } from '../Labels/LabelsHookHelper';
import { createNumberCellFormatter } from '../datagrid/cellFormatters/NumberCellFormatter';
import {
  createQueryPreservingReplacingLinkCellFormatter,
} from '../datagrid/cellFormatters/QueryPreservingLinkCellFormatter';
import { SvgFieldIcon } from '../field/components/SvgFieldIcon';
import { FieldNameMappings } from '../field/functions/FieldNameMapping';
import { FieldIcon } from '../field/types/FieldIcon';
import { DescriptionIconFromRowData, DescriptionInheritedType, DescriptionTextFromRowData, RowDataType } from '../fieldColumn/DescriptionInfo';
import { getNameWithApplicableDataType } from '../sharedDisplayGrid/FieldsDisplayGrid';
import { FieldsValidSortFields } from '../sorting/sortProviders/FieldsGridSortProvider';

export interface WorkbookFieldsDisplayGridRowData {
  readonly workbookId: string;
  readonly name: string | null;
  readonly dataType: FieldDataType | null;
  readonly id: string;
  readonly description?: string | null;
  readonly descriptionInherited?: (DescriptionInheritedType | null)[] | null;
  readonly fieldIcon: FieldIcon | null;
  readonly sheetCount: number | null;
  readonly labels?: ReadonlyArray<GraphLabel>
}

type WorkbookFieldsColumnFactory = ColumnFactory<
  GridSortProps<FieldsValidSortFields>,
  WorkbookFieldsDisplayGridRowData,
  FieldsValidSortFields
>;

function columns(siteUrls: SiteUrls): ReadonlyArray<WorkbookFieldsColumnFactory> {
  const columns: ReadonlyArray<WorkbookFieldsColumnFactory> = [
    () => createSelectionColumn(),
    (sortParams) => ({
      name: 'type',
      dataKey: UnsortableColumnKey,
      headerText: Messages.GridHeaderType(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createIconCellFormatter(
        ({ fieldIcon }) => <SvgFieldIcon fieldIcon={fieldIcon} />,
        ({ fieldIcon }) => fieldIcon ? FieldNameMappings[fieldIcon]() : ''
      ),
      sortable: false,
      resizable: false
    }),
    () => createLabelGridColumnFormatter(siteUrls, 'WARNING'),
    (sortParams) => ({
      name: 'name',
      dataKey: FieldOrderField.NAME,
      headerText: Messages.GridHeaderName(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createQueryPreservingReplacingLinkCellFormatter<WorkbookFieldsDisplayGridRowData>(
        getNameWithApplicableDataType,
        (urls, { id, workbookId }) => urls.workbookLineageFieldPath(workbookId, id),
      ),
      sortable: true,
    }),
    (sortParams) => ({
      name: 'sheets',
      dataKey: UnsortableColumnKey,
      headerText: Messages.GridHeaderSheets(),
      headerFormatter: createSortableHeaderFormatter(sortParams, 'right'),
      cellFormatter: createNumberCellFormatter(({ sheetCount }) => sheetCount),
      sortable: false,
    }),
    (sortParams) => ({
      name: 'description',
      dataKey: UnsortableColumnKey,
      headerText: Messages.GridHeaderDescription(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: descriptionCellFormatter(),
      sortable: false,
      growToFit: true,
    }),
    () => createLabelGridColumnFormatter(siteUrls, 'SENSITIVITY'),
  ];

  return columns.filter(Boolean) as ReadonlyArray<WorkbookFieldsColumnFactory>;
}

export type WorkbookFieldsDisplayGridProps =
  UsualDisplayGridProps<WorkbookFieldsDisplayGridRowData, GraphQLError> &
  SelectionProviderChildProps &
  GridSortProps<FieldsValidSortFields>;

export function WorkbookFieldsDisplayGrid(props: WorkbookFieldsDisplayGridProps): JSX.Element {
  const { customSensitivityLabelsEnabled } = React.useContext(FeatureFlagsContext);
  const siteUrls = React.useContext(SiteUrlsContext);
  const cols = columns(siteUrls)
    .map(columnFactory => columnFactory(props))
    .filter(column => {
      if (column.name === 'label-sensitivity' && !customSensitivityLabelsEnabled) {
        return false;
      }
      return true;
    });

  return (
    <ExplorerStyledDisplayGrid
      columns={cols}
      rowStore={props.rowStore}
      selectedKeys={props.selectedKeys}
      updateSelectionForKey={props.updateSelectionForKey}
      rowCount={props.rowCount}
      renderErrors={reportGraphQLErrors}
      orderBy={props.orderBy}
      updateSortOrder={props.updateSortOrder}
    />
  );
}

// Returns a cell formatter that displays a description and, if applicable, a description with inheritance info
function descriptionCellFormatter(): React.Factory<BodyCell.Props> & React.Factory<TypedBodyCellProps<WorkbookFieldsDisplayGridRowData>> {
  return createTextCellFormatter(
    descriptionTextFromRowData,
    Messages.NoDescriptionGridPlaceholder(), // placeholder
    () => false, // getDisabledFromRowData (always enabled)
    false, // noPadding
    descriptionIconFromRowData,
  );
}

// Call the helper methods with only the needed fields. I can't call the helper methods
// with a broader row-data type because createTextCellFormatter mandates that the interfaces
// of the helper methods accept the same broad type.
function minifyRowData(rowData: WorkbookFieldsDisplayGridRowData): RowDataType {
  return {
    description: rowData.description,
    descriptionInherited: rowData.descriptionInherited,
  };
}

function descriptionTextFromRowData(rowData: WorkbookFieldsDisplayGridRowData) {
  return DescriptionTextFromRowData(minifyRowData(rowData));
}

function descriptionIconFromRowData(rowData: WorkbookFieldsDisplayGridRowData) {
  return DescriptionIconFromRowData(minifyRowData(rowData));
}

function getLabelContentFromRowData(rowData: WorkbookFieldsDisplayGridRowData): LabelContent {
  return {
    contentId: rowData.id,
    contentType: null,
    contentName: rowData.name ?? undefined,
    subjectArea: 'Field'
  };
}

function createLabelGridColumnFormatter(urls: SiteUrls, category: LabelGridWrapperProps['category']): TypedColumn<WorkbookFieldsDisplayGridRowData, FieldsValidSortFields> {

  function getPropsFromRowData(rowData: WorkbookFieldsDisplayGridRowData): UseRenderLabelsParams {
    const content: LabelContent = getLabelContentFromRowData(rowData);
    const labels = rowData.labels ? rowData.labels : [];
    return getLabelParamsForGrids({ category, labels, urls, source: 'workbook-fields', content });
  }

  return createLabelColumnFormatter(labelCategoryToString(category), { overrides: {}, getPropsFromRowData }) as
    TypedColumn<WorkbookFieldsDisplayGridRowData, FieldsValidSortFields>;
}
